<template>
    <nav
        class="gw-nav-menu"
        :class="hasPartnerNav ? 'has-partner' : ''"
        :style="[isRtlLanguageFunc($globalConstants.host.language_code)]"
    >
        <div class="top-section">
            <div class="container">
                <div class="logo" v-if="!hasPartnerNav">
                    <router-link
                        :to="{ path: fullPath('/') }"
                        @click.native.prevent="onLogoClick"
                    >
                        <img
                            class="logo"
                            rel="preload"
                            v-lazy="logoSrc"
                            :width="160"
                            :height="42"
                            alt="logo"
                            fetchpriority="high"
                        />
                    </router-link>
                </div>

                <div class="nav__menu">
                    <ul
                        class="nav__list"
                        :style="{ gap: navItems.length <= 6 ? '40px' : '70px' }"
                    >
                        <li
                            class="nav__item"
                            v-for="item in navItems"
                            :key="item.id"
                        >
                            <router-link
                                class="nav__link"
                                :to="fullPath(item.route)"
                            >
                                <span>{{ $t(item.name) }}</span>
                                <svg
                                    v-if="
                                        item?.dropdown_items &&
                                        item?.dropdown_items.length > 0
                                    "
                                    aria-hidden="true"
                                    class="chevron"
                                    focusable="false"
                                    data-prefix="fas"
                                    data-icon="chevron-down"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                >
                                    <path
                                        fill="currentColor"
                                        d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"
                                    ></path>
                                </svg>
                            </router-link>

                            <ul
                                class="nav__list-child"
                                v-if="
                                    item.dropdown_items &&
                                    item.dropdown_items.length > 0
                                "
                            >
                                <li
                                    class="nav__item-child"
                                    v-for="item in item.dropdown_items"
                                    :key="item.id"
                                >
                                    <router-link
                                        class="nav__link-child"
                                        :to="fullPath(item.route)"
                                    >
                                        {{ $t(item.name) }}
                                    </router-link>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div class="top__menu">
                    <div class="search-slot">
                        <SearchBar
                            v-if="$globalConstants.host['adsense']"
                        ></SearchBar>
                    </div>

                    <LanguagePicker
                        v-if="
                            $globalConstants.host?.supported_languages.length >
                            1
                        "
                        :language-options="
                            $globalConstants.host?.supported_languages
                        "
                    />

                    <div class="burger-menu" @click="toggleDrawer()">
                        <div class="bar"></div>
                        <div class="bar"></div>
                        <div class="bar"></div>
                    </div>
                </div>
            </div>
        </div>

        <DrawerMenu
            v-if="isMobileDevice()"
            :navItems="navItems"
            :drawer-opened="drawerOpened"
            @drawerToggled="toggleDrawer()"
        />
    </nav>
</template>

<script>
import {
    navigationItemsMixin,
    mediaQueriesMixin,
    helper,
    contentDirectionMixin,
} from '@/ghost-mixins';
import LanguagePicker from './LanguagePicker';
import DrawerMenu from '@organisms/Navigation/GhostWriterNav/DrawerMenu';
import SearchBar from '@molecules/SearchBar/SearchBar.vue';
import { store } from '@/store';

export default {
    name: 'GhostWriterNav',
    mixins: [
        navigationItemsMixin,
        mediaQueriesMixin,
        helper,
        contentDirectionMixin,
    ],
    components: {
        LanguagePicker,
        DrawerMenu,
        SearchBar,
    },
    data() {
        const src = this.$globalConstants.host['header_banner_dark']
            ? this.$globalConstants.host['header_banner_dark']
            : this.$globalConstants.host['header_banner'];
        return {
            logoSrc: this.resizeImage(src, 260, 60, 100),
            drawerOpened: false,
        };
    },
    computed: {
        hasPartnerNav() {
            return this.$globalConstants.host.rtl_header;
        },
    },
    methods: {
        toggleDrawer() {
            this.drawerOpened = !this.drawerOpened;
        },
        onLogoClick() {
            const layoutType = store.state.layoutType;

            if (layoutType && layoutType !== 'landing_page') {
                this.$router.replace(this.fullPath('/blog'));
                return;
            }

            const homePath = this.fullPath('/');
            if (this.$route.path !== homePath) {
                this.$router.push(homePath);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';

.gw-nav-menu {
    width: 100%;
    position: relative;
    z-index: 10;
    border: 1px solid var(--border-color);

    &.has-partner {
        .search-slot {
            display: flex;
            justify-content: flex-end;
        }

        .top-section .container .top__menu {
            justify-content: flex-end;
            width: 100%;
        }
    }

    .top-section {
        background: var(--white);
        height: 52px;
        z-index: 2;
        position: relative;

        .container {
            height: 100%;
            margin: auto;
            max-width: 1280px;
            padding: 0 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            @include mq('tablet-wide') {
                padding: 0 32px;
            }

            .logo {
                width: 160px;
                height: 42px;
                display: flex;
                align-items: center;
            }

            .logo img.logo {
                width: 160px;
                height: 42px;
                object-fit: contain;
                display: block;
            }

            .nav__menu {
                display: none;
                @include mq('tablet-wide') {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    margin-left: 30px;
                }

                .nav__list {
                    display: flex;
                    list-style: none;
                    padding: 0;
                    width: 100%;
                    flex-direction: row;
                    justify-content: center;
                    margin: auto;
                    gap: 70px;

                    .nav__item {
                        position: relative;

                        .nav__link {
                            padding: 14px 0;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 6px;
                            cursor: pointer;
                            text-decoration: none;
                            color: var(--primary);
                            position: relative;
                            font-weight: 400;
                            z-index: 1;
                            width: max-content;
                            font-size: 14px;
                            .chevron {
                                width: 8px;
                                color: var(--primary);
                            }
                        }

                        .nav__list-child {
                            list-style-type: none;
                            position: absolute;
                            z-index: 1;
                            display: none;
                            grid-auto-columns: 290px;
                            grid-template-rows: repeat(7, auto);
                            grid-auto-flow: column;
                            background: var(--white);
                            filter: drop-shadow(0 10px 15px rgb(0 0 0 / 0.1))
                                drop-shadow(0 4px 6px rgb(0 0 0 / 0.1));

                            transform: translate(50%, 0);
                            border-radius: 6px;
                            top: 100%;
                            right: 50%;
                            column-gap: 20px;
                            padding: 10px;

                            .nav__item-child {
                                position: relative;
                                padding: 0 10px;

                                &:first-of-type,
                                &:nth-child(8) {
                                    border-top: none;
                                }

                                &::before {
                                    content: '';
                                    position: absolute;
                                    top: 0;
                                    width: 100%;
                                    height: 100%;
                                    background: transparent;
                                    z-index: -1;
                                    left: 0;
                                    border-radius: 8px;
                                }

                                .nav__link-child {
                                    position: relative;
                                    display: flex;
                                    align-items: center;
                                    justify-content: flex-start;
                                    gap: 10px;
                                    padding-top: 19px;
                                    padding-bottom: 19px;
                                    padding-inline-end: 16px;
                                    padding-inline-start: 6px;
                                    font-size: 14px;
                                    line-height: 17px;
                                    color: var(--primary);
                                }
                            }
                        }
                    }

                    .nav__item:hover {
                        .nav__link .chevron {
                            transform: rotate(180deg);
                        }

                        .nav__item-child:hover::before {
                            background: var(--tertiary-background);
                        }

                        .nav__list-child {
                            display: grid;
                        }
                    }
                }
            }

            .top__menu {
                display: flex;
                align-items: center;

                @include mq('desktop') {
                    gap: 35px;
                }

                .burger-menu {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    width: 20px;
                    height: 14px;
                    cursor: pointer;
                    margin-left: 24px;
                    @include mq('tablet-wide') {
                        display: none;
                    }

                    .bar {
                        width: 100%;
                        height: 2px;
                        background-color: var(--primary);
                    }
                }
            }
        }
    }

    .search-bar ::v-deep {
        .button {
            box-shadow: none;
            border: 1px solid var(--border-color);
            border-radius: 8px;
            color: var(--primary);
        }

        .search-input {
            border: 1px solid var(--border-color);
            border-radius: 8px;
        }

        .input-bar {
            height: 57px;
            width: 100% !important;
            padding: 8px 10px;
            left: 0;
            right: 0;
            top: 53px;
            background-color: var(--tertiary-background);
            border-bottom: 1px solid var(--border-color);
            box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 0 #0000,
                0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);

            @include mq('tablet') {
                padding: 8px 32px;
            }
        }

        .form-container {
            max-width: 1280px;
            margin: 0 auto;
            padding: 0;

            @include mq('tablet') {
                padding: 0 32px;
            }

            .button {
                height: 35px;
                width: 35px;
                background: var(--highlight);
                border: 1px solid var(--highlight);
                color: var(--white);
            }
        }

        .search-input {
            max-width: 100%;
            width: 100%;
            height: 35px;
            padding: 0 8px;

            &:focus {
                outline: none;
            }
        }
    }
    .search-slot {
        width: 48px;
        min-height: 32px;

        @include mq('tablet-wide') {
            width: 96px;
        }
    }
}
</style>
